/**
 * Shelfmate App - JavaScript
 * Handles drag & drop from product panel → canvas,
 * repositioning items on canvas, dimension rulers, canvas resize,
 * zoom in/out of items (1 cm = 5 px base scale), spacing snap to 5cm, and overlap prevention.
 *
 * MIGRATED:
 * - Dynamic price calculations
 * - Automatic discount scheme (10% >= €100, 20% >= €500)
 * - Order details drawer list & totals update
 * - Reset confirmation modal
 * - LocalStorage composition persistence
 * - AJAX cart checkout integration
 */

(function () {
  'use strict';

  /* =============================================
     STATE
     ============================================= */
  const savedZoom = parseFloat(localStorage.getItem('shelfmate_zoom'));
  const initialZoom = (!isNaN(savedZoom) && savedZoom >= 0.5 && savedZoom <= 4.0) ? savedZoom : 1.0;

  const state = {
    canvasW: 150,   // cm (logical, calculated dynamically from canvas container size)
    canvasH: 100,   // cm (logical, calculated dynamically from canvas container size)
    items: [],      // { id, productId, title, price, priceRaw, image, widthCm, heightCm, xCm, yCm, variantId, el }
    nextId: 1,
    dragging: null, // { type: 'panel'|'canvas', data, ghost, offsetX, offsetY }
    zoom: initialZoom, // zoom level factor (default 1.0, 1 cm = 5 px)
    showRulers: true, // ruler toggle visibility state
    selectedIds: new Set(), // Set of selected canvas item IDs
    detailsItemsCollapsed: true, // Show more/less toggle state
    discountPopupClosed: false, // Tracks if user dismissed the promo card
  };

  /* =============================================
     SELECTION MANAGEMENT
     ============================================= */
  function clearSelection() {
    state.selectedIds.clear();
    updateSelectionUI();
  }

  function selectItem(id, append) {
    if (!append) {
      state.selectedIds.clear();
    }
    state.selectedIds.add(id);
    updateSelectionUI();
  }

  function deselectItem(id) {
    state.selectedIds.delete(id);
    updateSelectionUI();
  }

  function toggleSelectItem(id) {
    if (state.selectedIds.has(id)) {
      state.selectedIds.delete(id);
    } else {
      state.selectedIds.add(id);
    }
    updateSelectionUI();
  }

  let groupSelectionBoxEl = null;

  function updateGroupBoundingBox() {
    if (!canvas) return;
    if (!groupSelectionBoxEl) {
      groupSelectionBoxEl = document.createElement('div');
      groupSelectionBoxEl.className = 'shelfmate-group-selection-box';
      enableGroupBoxDrag(groupSelectionBoxEl);
      canvas.appendChild(groupSelectionBoxEl);
    }

    if (state.selectedIds.size < 2) {
      groupSelectionBoxEl.style.display = 'none';
      return;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    state.items.forEach(function (item) {
      if (state.selectedIds.has(item.id)) {
        if (item.xCm < minX) minX = item.xCm;
        if (item.xCm + item.widthCm > maxX) maxX = item.xCm + item.widthCm;
        if (item.yCm < minY) minY = item.yCm;
        if (item.yCm + item.heightCm > maxY) maxY = item.yCm + item.heightCm;
      }
    });

    if (minX === Infinity) {
      groupSelectionBoxEl.style.display = 'none';
      return;
    }

    const paddingCm = 1.5; // 1.5cm padding around selected block
    const zoomFactor = pxPerCm * state.zoom;

    const boxLeftPx = Math.max(0, (minX - paddingCm) * zoomFactor);
    const boxTopPx = Math.max(0, (minY - paddingCm) * zoomFactor);
    const boxWidthPx = (maxX - minX + paddingCm * 2) * zoomFactor;
    const boxHeightPx = (maxY - minY + paddingCm * 2) * zoomFactor;

    groupSelectionBoxEl.style.left = boxLeftPx + 'px';
    groupSelectionBoxEl.style.top = boxTopPx + 'px';
    groupSelectionBoxEl.style.width = boxWidthPx + 'px';
    groupSelectionBoxEl.style.height = boxHeightPx + 'px';
    groupSelectionBoxEl.style.display = 'block';
  }

  function enableGroupBoxDrag(boxEl) {
    boxEl.addEventListener('pointerdown', function (e) {
      if (e.button !== 0 && e.pointerType !== 'touch') return;
      e.preventDefault();
      e.stopPropagation();

      const selectedItems = state.items.filter(function (it) {
        return state.selectedIds.has(it.id);
      });
      if (selectedItems.length === 0) return;

      const startX = e.clientX;
      const startY = e.clientY;

      boxEl.classList.add('is-dragging');

      const initialPositions = selectedItems.map(function (it) {
        return {
          id: it.id,
          item: it,
          startXCm: it.xCm,
          startYCm: it.yCm,
          startLeftPx: (parseFloat(it.el.style.left) || 0) / state.zoom,
          startTopPx: (parseFloat(it.el.style.top) || 0) / state.zoom,
        };
      });

      let initGroupMinX = Infinity;
      let initGroupMaxX = -Infinity;
      let initGroupMinY = Infinity;
      let initGroupMaxY = -Infinity;

      initialPositions.forEach(function (pos) {
        if (pos.startXCm < initGroupMinX) initGroupMinX = pos.startXCm;
        if (pos.startXCm + pos.item.widthCm > initGroupMaxX) initGroupMaxX = pos.startXCm + pos.item.widthCm;
        if (pos.startYCm < initGroupMinY) initGroupMinY = pos.startYCm;
        if (pos.startYCm + pos.item.heightCm > initGroupMaxY) initGroupMaxY = pos.startYCm + pos.item.heightCm;
      });

      selectedItems.forEach(function (it) {
        if (it.el) {
          it.el.classList.add('is-dragging-on-canvas');
          it.el.style.zIndex = 100;
        }
      });

      function onMove(ev) {
        const dx = (ev.clientX - startX) / state.zoom;
        const dy = (ev.clientY - startY) / state.zoom;

        let dxCm = dx / pxPerCm;
        let dyCm = dy / pxPerCm;

        dxCm = Math.max(-initGroupMinX, Math.min(state.canvasW - initGroupMaxX, dxCm));
        dyCm = Math.max(-initGroupMinY, Math.min(state.canvasH - initGroupMaxY, dyCm));

        initialPositions.forEach(function (pos) {
          const targetXCm = pos.startXCm + dxCm;
          const targetYCm = pos.startYCm + dyCm;

          if (pos.item.el) {
            pos.item.el.style.left = (targetXCm * pxPerCm * state.zoom) + 'px';
            pos.item.el.style.top = (targetYCm * pxPerCm * state.zoom) + 'px';
          }
          pos.item.xCm = targetXCm;
          pos.item.yCm = targetYCm;
        });

        updateGroupBoundingBox();
      }

      function onUp() {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        boxEl.classList.remove('is-dragging');

        selectedItems.forEach(function (it) {
          if (it.el) {
            it.el.classList.remove('is-dragging-on-canvas');
            it.el.style.zIndex = '';
          }
        });

        // Group Collision & Snap Verification
        let hasCollisionOrOutOfBounds = false;

        if (selectedItems.length === 1) {
          const single = selectedItems[0];
          const snapped = getSnappedPosition(single.id, single.xCm, single.yCm, single.widthCm, single.heightCm);
          if (!isOverlapping(single.id, snapped.x, snapped.y, single.widthCm, single.heightCm)) {
            single.xCm = snapped.x;
            single.yCm = snapped.y;
          }
        }

        const selectedIdSet = new Set(selectedItems.map(function (it) { return it.id; }));
        for (let i = 0; i < selectedItems.length; i++) {
          const sel = selectedItems[i];
          const rect = { x: sel.xCm, y: sel.yCm, w: sel.widthCm, h: sel.heightCm };

          for (let j = 0; j < state.items.length; j++) {
            const other = state.items[j];
            if (selectedIdSet.has(other.id)) continue;
            const otherRect = { x: other.xCm, y: other.yCm, w: other.widthCm, h: other.heightCm };
            if (intersects(rect, otherRect)) {
              hasCollisionOrOutOfBounds = true;
              break;
            }
          }
          if (hasCollisionOrOutOfBounds) break;
        }

        if (hasCollisionOrOutOfBounds) {
          canvas.classList.add('flash-invalid');
          setTimeout(function () { canvas.classList.remove('flash-invalid'); }, 300);

          initialPositions.forEach(function (pos) {
            pos.item.xCm = pos.startXCm;
            pos.item.yCm = pos.startYCm;
            if (pos.item.el) {
              pos.item.el.style.left = (pos.startXCm * pxPerCm * state.zoom) + 'px';
              pos.item.el.style.top = (pos.startYCm * pxPerCm * state.zoom) + 'px';
            }
          });
        } else {
          initialPositions.forEach(function (pos) {
            if (pos.item.el) {
              pos.item.el.style.left = (pos.item.xCm * pxPerCm * state.zoom) + 'px';
              pos.item.el.style.top = (pos.item.yCm * pxPerCm * state.zoom) + 'px';
            }
          });
        }

        updateGroupBoundingBox();
        updateRulers();
        updateTotals();
      }

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    });
  }

  function updateSelectionUI() {
    state.items.forEach(function (item) {
      if (item.el) {
        if (state.selectedIds.has(item.id)) {
          item.el.classList.add('is-selected');
        } else {
          item.el.classList.remove('is-selected');
        }
      }
    });
    updateGroupBoundingBox();
  }

  /* =============================================
     ELEMENTS
     ============================================= */
  const app = document.getElementById('shelfmate-app');
  const canvas = document.getElementById('shelfmate-canvas');
  const canvasHint = document.getElementById('shelfmate-canvas-hint');
  const badgeW = document.getElementById('badge-w');
  const badgeH = document.getElementById('badge-h');
  const labelW = document.getElementById('canvas-width-label');
  const labelH = document.getElementById('canvas-height-label');
  const rulerTop = document.getElementById('shelfmate-ruler-top');
  const rulerLeft = document.getElementById('shelfmate-ruler-left');
  const measureBtn = document.getElementById('shelfmate-measure-btn');

  // Toolbar Buttons
  const resetBtn = document.getElementById('shelfmate-reset-btn');

  // Zoom Elements
  const zoomInBtn = document.getElementById('shelfmate-zoom-in');
  const zoomOutBtn = document.getElementById('shelfmate-zoom-out');
  const zoomValLabel = document.getElementById('shelfmate-zoom-val');

  // Checkout Header & Details elements
  const checkoutHeader = document.getElementById('shelfmate-checkout-header');
  const totalPriceLabel = document.getElementById('shelfmate-total-price');
  const checkoutBtn = document.getElementById('shelfmate-checkout-btn');
  const detailsBtn = document.getElementById('shelfmate-details-btn');

  // Details Drawer elements
  const detailsDrawer = document.getElementById('shelfmate-details-drawer');
  const detailsCloseBtn = document.getElementById('shelfmate-details-close-btn');
  const detailsItemsContainer = document.getElementById('shelfmate-details-items');
  const detailsCount = document.getElementById('shelfmate-details-count');
  const detailsSubtotal = document.getElementById('shelfmate-details-subtotal');
  const detailsDiscountRow = document.getElementById('shelfmate-details-discount-row');
  const detailsDiscount = document.getElementById('shelfmate-details-discount');
  const detailsTotal = document.getElementById('shelfmate-details-total');
  const detailsCheckoutBtn = document.getElementById('shelfmate-details-checkout-btn');
  const detailsMoreWrapper = document.getElementById('shelfmate-details-more-wrapper');
  const detailsMoreBtn = document.getElementById('shelfmate-details-more-btn');
  const discountPopupWrap = document.getElementById('shelfmate-discount-popup-wrap');
  const discountPopupClose = document.getElementById('shelfmate-discount-popup-close');

  // Mobile Header & Submenu elements
  const mobileMenuWrapper = document.getElementById('shelfmate-mobile-menu-wrapper');
  const mobileSubmenu = document.getElementById('shelfmate-mobile-submenu');
  const mobileSubmenuBack = document.getElementById('shelfmate-mobile-submenu-back');
  const mobileSubmenuTitle = document.getElementById('shelfmate-mobile-submenu-title');
  const mobileSubmenuCount = document.getElementById('shelfmate-mobile-submenu-count');
  const mobileSubmenuProducts = document.getElementById('shelfmate-mobile-submenu-products');

  // Mobile Pinterest elements
  const mobilePinterestPopup = document.getElementById('shelfmate-mobile-pinterest-popup');
  const mobilePinterestPopupClose = document.getElementById('shelfmate-mobile-pinterest-popup-close');

  // Mobile Footer & Details elements
  const mobileFooterPrice = document.getElementById('shelfmate-mobile-footer-price');
  const mobileFooterDetailsBtn = document.getElementById('shelfmate-mobile-footer-details-btn');
  const mobileFooterCheckoutBtn = document.getElementById('shelfmate-mobile-footer-checkout-btn');

  // Mobile Order Details Drawer elements
  const mobileOrderDetail = document.getElementById('shelfmate-mobile-order-detail');
  const mobileOrderDetailClose = document.getElementById('shelfmate-mobile-order-detail-close');
  const mobileOrderDetailCount = document.getElementById('shelfmate-mobile-order-detail-count');
  const mobileOrderDetailItems = document.getElementById('shelfmate-mobile-order-detail-items');
  const mobileOrderDetailSubtotal = document.getElementById('shelfmate-mobile-order-detail-subtotal');
  const mobileOrderDetailDiscountRow = document.getElementById('shelfmate-mobile-order-detail-discount-row');
  const mobileOrderDetailDiscount = document.getElementById('shelfmate-mobile-order-detail-discount');
  const mobileOrderDetailTotal = document.getElementById('shelfmate-mobile-order-detail-total');
  const mobileDrawerCheckoutBtn = document.getElementById('shelfmate-mobile-drawer-checkout-btn');
  const mobileDetailsMoreWrapper = document.getElementById('shelfmate-mobile-order-detail-more-wrapper');
  const mobileDetailsMoreBtn = document.getElementById('shelfmate-mobile-order-detail-more-btn');

  // Reset Modal elements
  const resetModal = document.getElementById('shelfmate-reset-modal');
  const resetModalOverlay = document.getElementById('shelfmate-reset-modal-overlay');
  const resetModalClose = document.getElementById('shelfmate-reset-modal-close');
  const resetCancelBtn = document.getElementById('shelfmate-reset-cancel');
  const resetConfirmBtn = document.getElementById('shelfmate-reset-confirm');

  // Template Modal elements
  const templateModal = document.getElementById('shelfmate-template-modal');
  const templateModalOverlay = document.getElementById('shelfmate-template-modal-overlay');
  const templateModalClose = document.getElementById('shelfmate-template-modal-close');
  const templateCancelBtn = document.getElementById('shelfmate-template-cancel');
  const templateConfirmBtn = document.getElementById('shelfmate-template-confirm');

  if (!app || !canvas) return;

  const currencySymbol = app.dataset.currencySymbol || '€';
  const allowDiscount = app.dataset.allowDiscount === 'true';

  /* =============================================
     CANVAS SIZING (Canvas fills 100% of container, 1 cm = 5 px)
     ============================================= */
  let pxPerCm = window.innerWidth <= 767 ? 1.5 : 3; // 1 cm is 3px on desktop, reduced 2x to 1.5px on mobile

  function applyCanvasSize() {
    // Canvas is full-bleed workspace filling the container
    canvas.style.width = window.innerWidth <= 767 ? '200%' : '100%';
    canvas.style.height = '100%';

    // Update scale dynamically based on screen width
    pxPerCm = window.innerWidth <= 767 ? 1.5 : 3;

    // Calculate logical wall dimensions in cm based on container dimensions and zoom/scale
    state.canvasW = canvas.clientWidth / (pxPerCm * state.zoom);
    state.canvasH = canvas.clientHeight / (pxPerCm * state.zoom);





    clampAllItems();
    updateRulers();
    updateGroupBoundingBox();
  }

  // Initial calculation
  applyCanvasSize();
  window.addEventListener('load', applyCanvasSize);
  window.addEventListener('resize', applyCanvasSize);

  /* =============================================
     MEASURE RULER TOGGLE LOGIC
     ============================================= */
  if (measureBtn) {
    if (state.showRulers) {
      measureBtn.classList.add('is-active');
    }
    measureBtn.addEventListener('click', function () {
      state.showRulers = !state.showRulers;
      if (state.showRulers) {
        measureBtn.classList.add('is-active');
      } else {
        measureBtn.classList.remove('is-active');
      }
      updateRulers();
    });
  }

  /* =============================================
     ZOOM IN / OUT LOGIC
     ============================================= */
  function getMaxAllowedZoom() {
    let maxZoom = 4.0;
    if (state.items && state.items.length > 0) {
      state.items.forEach(function (item) {
        const rightEdge = item.xCm + item.widthCm;
        const bottomEdge = item.yCm + item.heightCm;
        if (rightEdge > 0) {
          const allowedX = canvas.clientWidth / (rightEdge * pxPerCm);
          if (allowedX < maxZoom) maxZoom = allowedX;
        }
        if (bottomEdge > 0) {
          const allowedY = canvas.clientHeight / (bottomEdge * pxPerCm);
          if (allowedY < maxZoom) maxZoom = allowedY;
        }
      });
    }
    return Math.max(0.5, maxZoom);
  }

  function applyZoom() {
    const maxAllowed = getMaxAllowedZoom();
    if (state.zoom > maxAllowed) {
      state.zoom = maxAllowed;
    }

    try {
      localStorage.setItem('shelfmate_zoom', state.zoom.toString());
    } catch (e) { }

    // Re-calculate the logical canvas dimensions and update items
    applyCanvasSize();
    if (zoomValLabel) {
      zoomValLabel.textContent = Math.round(state.zoom * 100) + '%';
    }

    if (zoomInBtn) {
      if (state.zoom >= maxAllowed - 0.001 || state.zoom >= 4.0) {
        zoomInBtn.style.opacity = '0.3';
        zoomInBtn.style.cursor = 'not-allowed';
      } else {
        zoomInBtn.style.opacity = '';
        zoomInBtn.style.cursor = '';
      }
    }
    if (zoomOutBtn) {
      if (state.zoom <= 0.5 + 0.001) {
        zoomOutBtn.style.opacity = '0.3';
        zoomOutBtn.style.cursor = 'not-allowed';
      } else {
        zoomOutBtn.style.opacity = '';
        zoomOutBtn.style.cursor = '';
      }
    }
  }

  if (zoomInBtn) {
    zoomInBtn.addEventListener('click', function () {
      const maxAllowed = getMaxAllowedZoom();
      if (state.zoom < maxAllowed) {
        state.zoom = Math.min(maxAllowed, state.zoom + 0.1);
        applyZoom();
      }
    });
  }

  if (zoomOutBtn) {
    zoomOutBtn.addEventListener('click', function () {
      if (state.zoom > 0.5) {
        state.zoom = Math.max(0.5, state.zoom - 0.1);
        applyZoom();
      }
    });
  }

  // Wheel to zoom inside container (directly without Ctrl key)
  const canvasContainer = canvas.parentElement;
  if (canvasContainer) {
    canvasContainer.addEventListener('wheel', function (e) {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.1 : -0.1;
      const maxAllowed = getMaxAllowedZoom();
      if (delta > 0 && state.zoom >= maxAllowed) return;
      if (delta < 0 && state.zoom <= 0.5) return;
      state.zoom = Math.max(0.5, Math.min(maxAllowed, state.zoom + delta));
      applyZoom();
    }, { passive: false });
  }

  // Touch pinch-to-zoom gesture on mobile (2-finger pinch)
  let touchStartDistance = 0;
  let initialZoomOnTouch = 1.0;

  function getTouchDistance(e) {
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  if (canvasContainer) {
    canvasContainer.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) {
        touchStartDistance = getTouchDistance(e);
        initialZoomOnTouch = state.zoom;
      }
    }, { passive: true });

    canvasContainer.addEventListener('touchmove', function (e) {
      if (e.touches.length === 2 && touchStartDistance > 0) {
        e.preventDefault();
        const currentDistance = getTouchDistance(e);
        const diff = currentDistance - touchStartDistance;
        const zoomDelta = diff / 150; // Divisor mengontrol sensitivitas zoom
        const maxAllowed = getMaxAllowedZoom();
        state.zoom = Math.max(0.5, Math.min(maxAllowed, initialZoomOnTouch + zoomDelta));
        applyZoom();
      }
    }, { passive: false });

    canvasContainer.addEventListener('touchend', function (e) {
      if (e.touches.length < 2) {
        touchStartDistance = 0;
      }
    }, { passive: true });
  }

  /* =============================================
     CANVAS SELECTION MARQUEE & CLICK DESELECT
     ============================================= */
  if (canvas) {
    canvas.addEventListener('pointerdown', function (e) {
      if (e.target.closest('.shelfmate-canvas-item') || e.target.closest('.shelfmate-toolbar')) return;
      if (e.button !== 0 && e.pointerType !== 'touch') return;

      const canvasRect = canvas.getBoundingClientRect();
      const startX = e.clientX - canvasRect.left;
      const startY = e.clientY - canvasRect.top;

      const isShift = e.shiftKey || e.metaKey || e.ctrlKey;
      if (!isShift) {
        clearSelection();
      }

      let marquee = null;
      let hasDragged = false;

      function onPointerMove(ev) {
        const currentX = ev.clientX - canvasRect.left;
        const currentY = ev.clientY - canvasRect.top;

        const dx = Math.abs(currentX - startX);
        const dy = Math.abs(currentY - startY);

        if (!hasDragged && (dx > 4 || dy > 4)) {
          hasDragged = true;
          marquee = document.createElement('div');
          marquee.className = 'shelfmate-selection-marquee';
          canvas.appendChild(marquee);
        }

        if (hasDragged && marquee) {
          const left = Math.min(startX, currentX);
          const top = Math.min(startY, currentY);
          const width = Math.abs(currentX - startX);
          const height = Math.abs(currentY - startY);

          marquee.style.left = left + 'px';
          marquee.style.top = top + 'px';
          marquee.style.width = width + 'px';
          marquee.style.height = height + 'px';

          const zoomFactor = pxPerCm * state.zoom;
          const boxXMin = left / zoomFactor;
          const boxXMax = (left + width) / zoomFactor;
          const boxYMin = top / zoomFactor;
          const boxYMax = (top + height) / zoomFactor;

          state.items.forEach(function (item) {
            const itemXMin = item.xCm;
            const itemXMax = item.xCm + item.widthCm;
            const itemYMin = item.yCm;
            const itemYMax = item.yCm + item.heightCm;

            const overlap = !(itemXMax <= boxXMin || itemXMin >= boxXMax || itemYMax <= boxYMin || itemYMin >= boxYMax);
            if (overlap) {
              state.selectedIds.add(item.id);
            } else if (!isShift) {
              state.selectedIds.delete(item.id);
            }
          });
          updateSelectionUI();
        }
      }

      function onPointerUp() {
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        document.removeEventListener('pointercancel', onPointerUp);

        if (marquee && marquee.parentNode) {
          marquee.parentNode.removeChild(marquee);
        }
      }

      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerUp);
    });
  }

  /* =============================================
     PRODUCT GROUP ACCORDION
     ============================================= */
  document.querySelectorAll('.shelfmate-group__header').forEach(function (header) {
    header.addEventListener('click', function (e) {
      if (e.target.classList.contains('shelfmate-group__checkbox')) return;
      const group = header.closest('.shelfmate-group');
      const groupId = header.dataset.groupId;
      const productList = document.getElementById('group-products-' + groupId);
      if (!group || !productList) return;

      const isCurrentlyOpen = group.classList.contains('is-open');

      // Close all other groups
      document.querySelectorAll('.shelfmate-group').forEach(function (g) {
        if (g !== group) {
          g.classList.remove('is-open');
          const otherHeader = g.querySelector('.shelfmate-group__header');
          if (otherHeader) {
            const otherGroupId = otherHeader.dataset.groupId;
            const otherProductList = document.getElementById('group-products-' + otherGroupId);
            if (otherProductList) {
              otherProductList.style.display = 'none';
            }
          }
        }
      });

      // Toggle current group
      if (isCurrentlyOpen) {
        group.classList.remove('is-open');
        productList.style.display = 'none';
      } else {
        group.classList.add('is-open');
        productList.style.display = 'flex';
      }
    });
  });

  // Group Type Filter Event Delegation
  document.addEventListener('click', function (e) {
    const tab = e.target.closest('.shelfmate-group-type-title');
    if (!tab) return;
    e.preventDefault();
    e.stopPropagation();

    const container = tab.closest('.shelfmate-group__products') || tab.closest('#shelfmate-mobile-submenu-products');
    if (!container) return;

    const header = tab.closest('.shelfmate-group-type-header');
    if (header) {
      header.querySelectorAll('.shelfmate-group-type-title').forEach(function (t) {
        t.classList.remove('is-active');
      });
    }
    tab.classList.add('is-active');

    const selectedType = tab.getAttribute('data-type');
    const cards = container.querySelectorAll('.shelfmate-product-card');
    cards.forEach(function (card) {
      const cardType = card.getAttribute('data-product-type') || 'Basic';
      if (cardType === selectedType) {
        card.style.display = '';
      } else {
        card.style.display = 'none';
      }
    });

    const mobileSubmenuCount = document.getElementById('shelfmate-mobile-submenu-count');
    if (mobileSubmenuCount && container.id === 'shelfmate-mobile-submenu-products') {
      const visibleCount = Array.from(cards).filter(function (card) {
        return card.style.display !== 'none';
      }).length;
      mobileSubmenuCount.textContent = visibleCount + ' Products';
    }
  });

  // Template Card Click Event (Desktop)
  document.addEventListener('click', function (e) {
    const card = e.target.closest('.shelfmate-template-card');
    if (!card || card.closest('#shelfmate-mobile-submenu-products')) return;
    e.preventDefault();

    const codeStr = card.getAttribute('data-code');
    if (!codeStr) return;
    try {
      const data = JSON.parse(codeStr);
      if (Array.isArray(data)) {
        // Dynamic lookup from sidebar
        data.forEach(function (itemData) {
          const sidebarCard = document.querySelector(`.shelfmate-product-card[data-variant-id="${itemData.variantId}"]`);
          if (sidebarCard) {
            itemData.title = sidebarCard.getAttribute('data-product-title') || '';
            itemData.price = sidebarCard.getAttribute('data-product-price') || '';
            itemData.priceRaw = parseFloat(sidebarCard.getAttribute('data-product-price-raw')) || 0;
            itemData.image = sidebarCard.getAttribute('data-product-image') || '';
          } else {
            itemData.title = itemData.title || '';
            itemData.price = itemData.price || '';
            itemData.priceRaw = itemData.priceRaw || 0;
            itemData.image = itemData.image || '';
          }
        });

        if (state.items.length > 0) {
          pendingTemplateData = data;
          if (templateModal) templateModal.style.display = 'flex';
        } else {
          state.items = [];
          state.selectedIds.clear();
          data.forEach(function (itemData) {
            addItemToCanvas(itemData, itemData.xCm + itemData.widthCm / 2, itemData.yCm + itemData.heightCm / 2);
          });
          applyZoom();
          updateTotals();
        }
      }
    } catch (err) {
      console.error("Failed to parse template code:", err);
    }
  });

  // Floating JSON Download Click Listener (Dev Only)
  const devDownloadBtn = document.getElementById('shelfmate-dev-download-btn');
  if (devDownloadBtn) {
    devDownloadBtn.addEventListener('click', function (e) {
      e.preventDefault();
      const dataToSave = state.items.map(function (item) {
        return {
          productId: item.productId,
          variantId: item.variantId,
          widthCm: item.widthCm,
          heightCm: item.heightCm,
          xCm: item.xCm,
          yCm: item.yCm
        };
      });
      const jsonString = JSON.stringify(dataToSave, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'shelfmate_composition.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  }

  /* =============================================
     COLLISION DETECT & 5CM SPACING SNAPPING
     ============================================= */

  function intersects(rect1, rect2) {
    const buffer = 0.05; // tiny overlap buffer to allow side-by-side touching
    return !(rect1.x + rect1.w - buffer <= rect2.x ||
      rect2.x + rect2.w - buffer <= rect1.x ||
      rect1.y + rect1.h - buffer <= rect2.y ||
      rect2.y + rect2.h - buffer <= rect1.y);
  }

  function isOverlapping(itemId, xCm, yCm, widthCm, heightCm) {
    const currentRect = { x: xCm, y: yCm, w: widthCm, h: heightCm };
    for (let i = 0; i < state.items.length; i++) {
      const other = state.items[i];
      if (other.id === itemId) continue;
      const otherRect = { x: other.xCm, y: other.yCm, w: other.widthCm, h: other.heightCm };
      if (intersects(currentRect, otherRect)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Snaps target position (xCm, yCm) based on other items.
   * If gap is between 1cm and 10cm, snap the gap to exactly 5cm.
   */
  function getSnappedPosition(itemId, xCm, yCm, widthCm, heightCm) {
    let snapX = xCm;
    let snapY = yCm;

    const thresholdMin = -10.0;
    const thresholdMax = 10.0;
    const targetSpacing = 5.0;

    for (let i = 0; i < state.items.length; i++) {
      const other = state.items[i];
      if (other.id === itemId) continue;

      const ox = other.xCm;
      const oy = other.yCm;
      const ow = other.widthCm;
      const oh = other.heightCm;

      // Check Y overlap to snap X (horizontal spacing snapping)
      const yOverlap = !(yCm + heightCm <= oy || oy + oh <= yCm);
      if (yOverlap) {
        // Dragged item is to the right of other
        const leftGap = snapX - (ox + ow);
        if (leftGap >= thresholdMin && leftGap <= thresholdMax) {
          snapX = ox + ow + targetSpacing;
        }
        // Dragged item is to the left of other
        const rightGap = ox - (snapX + widthCm);
        if (rightGap >= thresholdMin && rightGap <= thresholdMax) {
          snapX = ox - widthCm - targetSpacing;
        }
      }

      // Check X overlap to snap Y (vertical spacing snapping)
      const xOverlap = !(xCm + widthCm <= ox || ox + ow <= xCm);
      if (xOverlap) {
        // Dragged item is below other
        const topGap = snapY - (oy + oh);
        if (topGap >= thresholdMin && topGap <= thresholdMax) {
          snapY = oy + oh + targetSpacing;
        }
        // Dragged item is above other
        const bottomGap = oy - (snapY + heightCm);
        if (bottomGap >= thresholdMin && bottomGap <= thresholdMax) {
          snapY = oy - heightCm - targetSpacing;
        }
      }
    }

    return { x: snapX, y: snapY };
  }

  /**
   * Finds a valid, non-overlapping position on canvas for a clicked sidebar item.
   * Prefers placing the new item with a 5cm gap relative to existing items.
   */
  function findNextPositionForClickedItem(widthCm, heightCm) {
    if (state.items.length === 0) {
      const centerX = Math.max(0, (state.canvasW - widthCm) / 2);
      const centerY = Math.max(0, (state.canvasH - heightCm) / 2);
      return { x: centerX, y: centerY };
    }

    const gap = 5.0; // 5cm spacing

    // Try candidates adjacent to existing items (starting from the most recently added item)
    const itemsReversed = state.items.slice().reverse();

    for (let i = 0; i < itemsReversed.length; i++) {
      const other = itemsReversed[i];

      const candidates = [
        // 1. Right side (aligned top)
        { x: other.xCm + other.widthCm + gap, y: other.yCm },
        // 2. Bottom side (aligned left)
        { x: other.xCm, y: other.yCm + other.heightCm + gap },
        // 3. Left side (aligned top)
        { x: other.xCm - widthCm - gap, y: other.yCm },
        // 4. Top side (aligned left)
        { x: other.xCm, y: other.yCm - heightCm - gap },
        // 5. Right side (aligned bottom)
        { x: other.xCm + other.widthCm + gap, y: other.yCm + other.heightCm - heightCm },
        // 6. Bottom side (aligned right)
        { x: other.xCm + other.widthCm - widthCm, y: other.yCm + other.heightCm + gap }
      ];

      for (let j = 0; j < candidates.length; j++) {
        const cand = candidates[j];
        const inBounds = (
          cand.x >= 0 &&
          cand.x + widthCm <= state.canvasW &&
          cand.y >= 0 &&
          cand.y + heightCm <= state.canvasH
        );

        if (inBounds && !isOverlapping(null, cand.x, cand.y, widthCm, heightCm)) {
          return { x: cand.x, y: cand.y };
        }
      }
    }

    // Fallback: Grid search across canvas with 5cm step
    const step = 5;
    for (let y = 0; y <= state.canvasH - heightCm; y += step) {
      for (let x = 0; x <= state.canvasW - widthCm; x += step) {
        if (!isOverlapping(null, x, y, widthCm, heightCm)) {
          return { x: x, y: y };
        }
      }
    }

    // Default fallback to center if no spot fits
    return {
      x: Math.max(0, (state.canvasW - widthCm) / 2),
      y: Math.max(0, (state.canvasH - heightCm) / 2)
    };
  }

  /* =============================================
     DRAG & DROP — From Panel to Canvas
     Custom pointer-based drag to support touch + mouse
     ============================================= */
  let ghost = null;

  function createGhost(card) {
    const widthCm = parseFloat(card.dataset.productWidth) || 50;
    const heightCm = parseFloat(card.dataset.productHeight) || 30;
    const targetW = widthCm * pxPerCm * state.zoom;
    const targetH = heightCm * pxPerCm * state.zoom;

    const g = document.createElement('div');
    g.className = 'shelfmate-drag-ghost';
    g.style.width = targetW + 'px';
    g.style.height = targetH + 'px';
    const img = card.querySelector('.shelfmate-product-card__img');
    if (img && img.src) {
      const gImg = document.createElement('img');
      gImg.src = img.src;
      g.appendChild(gImg);
    }
    document.body.appendChild(g);
    return g;
  }

  function moveGhost(g, x, y) {
    if (!g) return;
    const w = parseInt(g.style.width, 10) || 60;
    const h = parseInt(g.style.height, 10) || 40;
    g.style.left = (x - w / 2) + 'px';
    g.style.top = (y - h / 2) + 'px';
  }

  function removeGhost(g) {
    if (g && g.parentNode) g.parentNode.removeChild(g);
  }

  /* ---- Panel Product Drag & Click Start ---- */
  document.querySelectorAll('.shelfmate-product-card').forEach(function (card) {
    if (card.classList.contains('shelfmate-template-card')) return;
    card.addEventListener('pointerdown', function (e) {
      if (e.button !== 0 && e.pointerType !== 'touch') return;
      e.preventDefault();

      const data = {
        productId: card.dataset.productId,
        title: card.dataset.productTitle,
        price: card.dataset.productPrice,
        priceRaw: parseFloat(card.dataset.productPriceRaw) || 0,
        image: card.dataset.productImage,
        variantId: card.dataset.variantId,
        widthCm: parseFloat(card.dataset.productWidth) || 50,
        heightCm: parseFloat(card.dataset.productHeight) || 30,
      };

      ghost = createGhost(card);
      moveGhost(ghost, e.clientX, e.clientY);
      card.classList.add('is-dragging');

      state.dragging = {
        type: 'panel',
        data: data,
        sourceCard: card,
        ghost: ghost,
        startX: e.clientX,
        startY: e.clientY
      };

      document.addEventListener('pointermove', onDragMove);
      document.addEventListener('pointerup', onDragEnd);
      document.addEventListener('pointercancel', onDragEnd);
    });
  });

  /* ---- Global Drag Move ---- */
  function onDragMove(e) {
    if (!state.dragging) return;
    moveGhost(state.dragging.ghost, e.clientX, e.clientY);

    const canvasRect = canvas.getBoundingClientRect();
    const over = (
      e.clientX >= canvasRect.left &&
      e.clientX <= canvasRect.right &&
      e.clientY >= canvasRect.top &&
      e.clientY <= canvasRect.bottom
    );
    canvas.classList.toggle('drag-over', over);
  }

  /* ---- Global Drag End ---- */
  function onDragEnd(e) {
    if (!state.dragging) return;

    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup', onDragEnd);
    document.removeEventListener('pointercancel', onDragEnd);

    removeGhost(state.dragging.ghost);
    canvas.classList.remove('drag-over');

    if (state.dragging.type === 'panel' && state.dragging.sourceCard) {
      state.dragging.sourceCard.classList.remove('is-dragging');
    }

    const canvasRect = canvas.getBoundingClientRect();
    const overCanvas = (
      e.clientX >= canvasRect.left &&
      e.clientX <= canvasRect.right &&
      e.clientY >= canvasRect.top &&
      e.clientY <= canvasRect.bottom
    );

    if (overCanvas) {
      if (state.dragging.type === 'panel') {
        const xPx = e.clientX - canvasRect.left;
        const yPx = e.clientY - canvasRect.top;
        // Divide by zoom and pxPerCm to translate pixel coordinates to logical cm
        const xCm = (xPx / state.zoom) / pxPerCm;
        const yCm = (yPx / state.zoom) / pxPerCm;

        const wCm = state.dragging.data.widthCm;
        const hCm = state.dragging.data.heightCm;
        let targetXCm = Math.max(0, Math.min(state.canvasW - wCm, xCm - wCm / 2));
        let targetYCm = Math.max(0, Math.min(state.canvasH - hCm, yCm - hCm / 2));

        // Snap before drop
        const snappedPos = getSnappedPosition(null, targetXCm, targetYCm, wCm, hCm);
        targetXCm = snappedPos.x;
        targetYCm = snappedPos.y;

        // Verify there is no overlap
        if (!isOverlapping(null, targetXCm, targetYCm, wCm, hCm)) {
          addItemToCanvas(state.dragging.data, targetXCm + wCm / 2, targetYCm + hCm / 2);
        } else {
          canvas.classList.add('flash-invalid');
          setTimeout(() => canvas.classList.remove('flash-invalid'), 300);
        }
      }
    } else if (state.dragging.type === 'panel') {
      // Check if it was a CLICK on the sidebar product card (not dragged onto canvas)
      const distanceMoved = Math.hypot(
        e.clientX - (state.dragging.startX || 0),
        e.clientY - (state.dragging.startY || 0)
      );

      if (distanceMoved < 8) {
        // User clicked the sidebar product card -> Find 5cm spaced position
        const data = state.dragging.data;
        const pos = findNextPositionForClickedItem(data.widthCm, data.heightCm);

        if (pos && !isOverlapping(null, pos.x, pos.y, data.widthCm, data.heightCm)) {
          addItemToCanvas(data, pos.x + data.widthCm / 2, pos.y + data.heightCm / 2);
        } else {
          canvas.classList.add('flash-invalid');
          setTimeout(() => canvas.classList.remove('flash-invalid'), 300);
        }
      }
    } else if (state.dragging.type === 'canvas') {
      removeItemFromCanvas(state.dragging.itemId);
    }

    state.dragging = null;
  }

  /* =============================================
     ADD ITEM TO CANVAS
     ============================================= */
  function addItemToCanvas(data, xCm, yCm) {
    const id = state.nextId++;
    applyCanvasSize();

    let itemX = 0;
    let itemY = 0;

    if (data.xCm !== undefined && !isNaN(data.xCm)) {
      itemX = data.xCm;
    } else if (xCm !== undefined && !isNaN(xCm)) {
      itemX = Math.max(0, Math.min(state.canvasW - data.widthCm, xCm - data.widthCm / 2));
    } else {
      const pos = findNextPositionForClickedItem(data.widthCm, data.heightCm);
      itemX = pos.x;
    }

    if (data.yCm !== undefined && !isNaN(data.yCm)) {
      itemY = data.yCm;
    } else if (yCm !== undefined && !isNaN(yCm)) {
      itemY = Math.max(0, Math.min(state.canvasH - data.heightCm, yCm - data.heightCm / 2));
    } else {
      const pos = findNextPositionForClickedItem(data.widthCm, data.heightCm);
      itemY = pos.y;
    }

    const el = document.createElement('div');
    el.className = 'shelfmate-canvas-item';
    el.dataset.id = id;
    el.style.left = (itemX * pxPerCm * state.zoom) + 'px';
    el.style.top = (itemY * pxPerCm * state.zoom) + 'px';
    el.style.width = (data.widthCm * pxPerCm * state.zoom) + 'px';
    el.style.height = (data.heightCm * pxPerCm * state.zoom) + 'px';

    if (data.image) {
      const img = document.createElement('img');
      img.src = data.image;
      img.alt = data.title;
      img.className = 'shelfmate-canvas-item__img';
      el.appendChild(img);
    } else {
      const ph = document.createElement('div');
      ph.className = 'shelfmate-canvas-item__placeholder';
      ph.innerHTML = '<svg viewBox="0 0 ' + data.widthCm + ' ' + data.heightCm + '" fill="none" stroke="#ccc" stroke-width="1"><rect x="1" y="1" width="' + (data.widthCm - 2) + '" height="' + (data.heightCm - 2) + '" rx="2"/></svg>';
      el.appendChild(ph);
    }

    const label = document.createElement('span');
    label.className = 'shelfmate-canvas-item__label';
    label.textContent = data.title;
    el.appendChild(label);

    const removeBtn = document.createElement('div');
    removeBtn.className = 'shelfmate-canvas-item__remove';
    removeBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#fff" stroke-width="1.5"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>';
    removeBtn.addEventListener('pointerdown', function (e) {
      e.stopPropagation();
      removeItemFromCanvas(id);
    });
    el.appendChild(removeBtn);

    enableCanvasDrag(el, id);

    canvas.appendChild(el);

    const item = {
      id, el,
      productId: data.productId,
      title: data.title,
      price: data.price,
      priceRaw: data.priceRaw,
      image: data.image,
      variantId: data.variantId,
      widthCm: data.widthCm,
      heightCm: data.heightCm,
      xCm: itemX,
      yCm: itemY,
    };
    state.items.push(item);

    updateHint();
    updateTotals();
  }

  /* =============================================
     CANVAS ITEM DRAG (reposition, group drag, snap & collision)
     ============================================= */
  function enableCanvasDrag(el, id) {
    el.addEventListener('pointerdown', function (e) {
      if (e.target.closest('.shelfmate-canvas-item__remove')) return;
      if (e.button !== 0 && e.pointerType !== 'touch') return;
      e.preventDefault();
      e.stopPropagation();

      const isShift = e.shiftKey || e.metaKey || e.ctrlKey;
      if (isShift) {
        toggleSelectItem(id);
        return;
      }

      // If item is not currently selected, select only this item
      if (!state.selectedIds.has(id)) {
        selectItem(id, false);
      }

      // Collect all items in the selection group
      const selectedItems = state.items.filter(function (it) {
        return state.selectedIds.has(it.id);
      });
      if (selectedItems.length === 0) return;

      const startX = e.clientX;
      const startY = e.clientY;

      // Store initial positions of all selected items
      const initialPositions = selectedItems.map(function (it) {
        return {
          id: it.id,
          item: it,
          startXCm: it.xCm,
          startYCm: it.yCm,
          startLeftPx: (parseFloat(it.el.style.left) || 0) / state.zoom,
          startTopPx: (parseFloat(it.el.style.top) || 0) / state.zoom,
        };
      });

      let initGroupMinX = Infinity;
      let initGroupMaxX = -Infinity;
      let initGroupMinY = Infinity;
      let initGroupMaxY = -Infinity;

      initialPositions.forEach(function (pos) {
        if (pos.startXCm < initGroupMinX) initGroupMinX = pos.startXCm;
        if (pos.startXCm + pos.item.widthCm > initGroupMaxX) initGroupMaxX = pos.startXCm + pos.item.widthCm;
        if (pos.startYCm < initGroupMinY) initGroupMinY = pos.startYCm;
        if (pos.startYCm + pos.item.heightCm > initGroupMaxY) initGroupMaxY = pos.startYCm + pos.item.heightCm;
      });

      // Highlight dragging state for all selected items
      selectedItems.forEach(function (it) {
        if (it.el) {
          it.el.classList.add('is-dragging-on-canvas');
          it.el.style.zIndex = 100;
        }
      });

      function onMove(ev) {
        const dx = (ev.clientX - startX) / state.zoom;
        const dy = (ev.clientY - startY) / state.zoom;

        let dxCm = dx / pxPerCm;
        let dyCm = dy / pxPerCm;

        dxCm = Math.max(-initGroupMinX, Math.min(state.canvasW - initGroupMaxX, dxCm));
        dyCm = Math.max(-initGroupMinY, Math.min(state.canvasH - initGroupMaxY, dyCm));

        initialPositions.forEach(function (pos) {
          const targetXCm = pos.startXCm + dxCm;
          const targetYCm = pos.startYCm + dyCm;

          if (pos.item.el) {
            pos.item.el.style.left = (targetXCm * pxPerCm * state.zoom) + 'px';
            pos.item.el.style.top = (targetYCm * pxPerCm * state.zoom) + 'px';
          }
          pos.item.xCm = targetXCm;
          pos.item.yCm = targetYCm;
        });
        updateGroupBoundingBox();
      }

      function onUp() {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);

        selectedItems.forEach(function (it) {
          if (it.el) {
            it.el.classList.remove('is-dragging-on-canvas');
            it.el.style.zIndex = '';
          }
        });

        // Group Collision & Snap Verification
        let hasCollisionOrOutOfBounds = false;

        // 1. If dragging single item, attempt snapping
        if (selectedItems.length === 1) {
          const single = selectedItems[0];
          const snapped = getSnappedPosition(single.id, single.xCm, single.yCm, single.widthCm, single.heightCm);
          if (!isOverlapping(single.id, snapped.x, snapped.y, single.widthCm, single.heightCm)) {
            single.xCm = snapped.x;
            single.yCm = snapped.y;
          }
        }

        // 2. Check overlap between selected items and non-selected items
        const selectedIdSet = new Set(selectedItems.map(function (it) { return it.id; }));
        for (let i = 0; i < selectedItems.length; i++) {
          const sel = selectedItems[i];
          const rect = { x: sel.xCm, y: sel.yCm, w: sel.widthCm, h: sel.heightCm };

          // Check against unselected items
          for (let j = 0; j < state.items.length; j++) {
            const other = state.items[j];
            if (selectedIdSet.has(other.id)) continue;
            const otherRect = { x: other.xCm, y: other.yCm, w: other.widthCm, h: other.heightCm };
            if (intersects(rect, otherRect)) {
              hasCollisionOrOutOfBounds = true;
              break;
            }
          }
          if (hasCollisionOrOutOfBounds) break;
        }

        // 3. Revert if collision occurred
        if (hasCollisionOrOutOfBounds) {
          canvas.classList.add('flash-invalid');
          setTimeout(function () { canvas.classList.remove('flash-invalid'); }, 300);

          initialPositions.forEach(function (pos) {
            pos.item.xCm = pos.startXCm;
            pos.item.yCm = pos.startYCm;
            if (pos.item.el) {
              pos.item.el.style.left = (pos.startXCm * pxPerCm * state.zoom) + 'px';
              pos.item.el.style.top = (pos.startYCm * pxPerCm * state.zoom) + 'px';
            }
          });
        } else {
          // Commit final positions
          initialPositions.forEach(function (pos) {
            if (pos.item.el) {
              pos.item.el.style.left = (pos.item.xCm * pxPerCm * state.zoom) + 'px';
              pos.item.el.style.top = (pos.item.yCm * pxPerCm * state.zoom) + 'px';
            }
          });
        }

        updateRulers();
        updateTotals();
      }

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    });
  }

  /* =============================================
     REMOVE ITEM FROM CANVAS
     ============================================= */
  function removeItemFromCanvas(id) {
    const idx = state.items.findIndex(function (it) { return it.id === id; });
    if (idx === -1) return;
    const item = state.items[idx];
    if (item.el && item.el.parentNode) {
      item.el.parentNode.removeChild(item.el);
    }
    state.items.splice(idx, 1);
    state.selectedIds.delete(id);
    updateSelectionUI();
    updateHint();
    updateTotals();
  }

  /* =============================================
     KEYBOARD SHORTCUTS (Delete / Backspace)
     ============================================= */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable)) {
        return;
      }
      if (state.selectedIds.size > 0) {
        const idsToRemove = Array.from(state.selectedIds);
        idsToRemove.forEach(function (id) {
          removeItemFromCanvas(id);
        });
        clearSelection();
      }
    }
  });

  /* =============================================
     RESET (WITH MODAL CONFIRMATION)
     ============================================= */
  if (resetBtn && resetModal) {
    resetBtn.addEventListener('click', function () {
      resetModal.style.display = 'flex';
    });
  }

  if (resetModalClose) {
    resetModalClose.addEventListener('click', function () {
      resetModal.style.display = 'none';
    });
  }

  if (resetModalOverlay) {
    resetModalOverlay.addEventListener('click', function () {
      resetModal.style.display = 'none';
    });
  }

  if (resetCancelBtn) {
    resetCancelBtn.addEventListener('click', function () {
      resetModal.style.display = 'none';
    });
  }

  if (resetConfirmBtn) {
    resetConfirmBtn.addEventListener('click', function () {
      state.items.forEach(function (item) {
        if (item.el && item.el.parentNode) {
          item.el.parentNode.removeChild(item.el);
        }
      });
      state.items = [];
      state.nextId = 1;
      state.selectedIds.clear();
      updateSelectionUI();
      localStorage.removeItem('shelfmate_composition');
      localStorage.removeItem('shelfmate_zoom');
      state.zoom = 1.0;
      applyZoom();
      resetModal.style.display = 'none';
      updateHint();
      updateTotals();
    });
  }

  /* =============================================
     TEMPLATE (WITH MODAL CONFIRMATION)
     ============================================= */
  let pendingTemplateData = null;

  if (templateModalClose) {
    templateModalClose.addEventListener('click', function () {
      if (templateModal) templateModal.style.display = 'none';
      pendingTemplateData = null;
    });
  }

  if (templateModalOverlay) {
    templateModalOverlay.addEventListener('click', function () {
      if (templateModal) templateModal.style.display = 'none';
      pendingTemplateData = null;
    });
  }

  if (templateCancelBtn) {
    templateCancelBtn.addEventListener('click', function () {
      if (templateModal) templateModal.style.display = 'none';
      pendingTemplateData = null;
    });
  }

  if (templateConfirmBtn) {
    templateConfirmBtn.addEventListener('click', function () {
      if (templateModal) templateModal.style.display = 'none';
      if (pendingTemplateData) {
        state.items.forEach(function (item) {
          if (item.el && item.el.parentNode) {
            item.el.parentNode.removeChild(item.el);
          }
        });
        state.items = [];
        state.selectedIds.clear();

        pendingTemplateData.forEach(function (itemData) {
          addItemToCanvas(itemData, itemData.xCm + itemData.widthCm / 2, itemData.yCm + itemData.heightCm / 2);
        });

        applyZoom();
        updateTotals();
      }
      pendingTemplateData = null;
    });
  }

  /* =============================================
     DETAILS DRAWER INTERACTION
     ============================================= */
  if (detailsBtn && detailsDrawer) {
    detailsBtn.addEventListener('click', function () {
      detailsDrawer.classList.add('active');
    });
  }

  if (detailsCloseBtn && detailsDrawer) {
    detailsCloseBtn.addEventListener('click', function () {
      detailsDrawer.classList.remove('active');
    });
  }

  if (detailsMoreBtn) {
    detailsMoreBtn.addEventListener('click', function () {
      state.detailsItemsCollapsed = !state.detailsItemsCollapsed;
      renderDetailsItems();
      renderMobileDetailsItems();
    });
  }

  if (mobileDetailsMoreBtn) {
    mobileDetailsMoreBtn.addEventListener('click', function () {
      state.detailsItemsCollapsed = !state.detailsItemsCollapsed;
      renderDetailsItems();
      renderMobileDetailsItems();
    });
  }

  if (discountPopupClose) {
    discountPopupClose.addEventListener('click', function () {
      state.discountPopupClosed = true;
      if (discountPopupWrap) {
        discountPopupWrap.style.display = 'none';
      }
    });
  }

  /* =============================================
     MOBILE INTERACTION HANDLERS (Gatsby Parity)
     ============================================= */
  if (mobilePinterestPopupClose && mobilePinterestPopup) {
    mobilePinterestPopupClose.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      mobilePinterestPopup.classList.add('is-hidden');
    });
  }

  // Fallback Event Delegation for the Pinterest popup close button
  document.addEventListener('click', function (e) {
    const closeBtn = e.target.closest('#shelfmate-mobile-pinterest-popup-close');
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();
      const popup = document.getElementById('shelfmate-mobile-pinterest-popup');
      if (popup) {
        popup.classList.add('is-hidden');
      }
    }
  });

  if (mobileMenuWrapper) {
    const pills = mobileMenuWrapper.querySelectorAll('.shelfmate-mobile-menu-pill');
    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        pills.forEach(function (p) { p.classList.remove('is-active'); });
        pill.classList.add('is-active');

        const groupId = pill.getAttribute('data-mobile-group-id');
        const groupName = pill.getAttribute('data-group-name');

        const targetGroupProducts = document.getElementById('group-products-' + groupId);
        if (targetGroupProducts && mobileSubmenuProducts) {
          mobileSubmenuProducts.innerHTML = '';

          let typeHeader = targetGroupProducts.querySelector('.shelfmate-group-type-header.for-mobile');
          if (!typeHeader) {
            typeHeader = targetGroupProducts.querySelector('.shelfmate-group-type-header:not(.for-desktop)');
          }
          if (!typeHeader) {
            typeHeader = targetGroupProducts.querySelector('.shelfmate-group-type-header');
          }
          if (typeHeader) {
            const headerClone = typeHeader.cloneNode(true);
            mobileSubmenuProducts.appendChild(headerClone);
          }

          const cards = targetGroupProducts.querySelectorAll('.shelfmate-product-card');

          if (mobileSubmenuTitle) mobileSubmenuTitle.textContent = groupName;

          cards.forEach(function (card) {
            const clone = card.cloneNode(true);
            clone.style.display = card.style.display;

            clone.addEventListener('click', function (e) {
              e.preventDefault();

              if (clone.classList.contains('shelfmate-template-card')) {
                const codeStr = clone.getAttribute('data-code');
                if (codeStr) {
                  try {
                    const data = JSON.parse(codeStr);
                    if (Array.isArray(data)) {
                      // Dynamic lookup from sidebar
                      data.forEach(function (itemData) {
                        const sidebarCard = document.querySelector(`.shelfmate-product-card[data-variant-id="${itemData.variantId}"]`);
                        if (sidebarCard) {
                          itemData.title = sidebarCard.getAttribute('data-product-title') || '';
                          itemData.price = sidebarCard.getAttribute('data-product-price') || '';
                          itemData.priceRaw = parseFloat(sidebarCard.getAttribute('data-product-price-raw')) || 0;
                          itemData.image = sidebarCard.getAttribute('data-product-image') || '';
                        } else {
                          itemData.title = itemData.title || '';
                          itemData.price = itemData.price || '';
                          itemData.priceRaw = itemData.priceRaw || 0;
                          itemData.image = itemData.image || '';
                        }
                      });

                      if (state.items.length > 0) {
                        pendingTemplateData = data;
                        if (templateModal) templateModal.style.display = 'flex';
                      } else {
                        state.items = [];
                        state.selectedIds.clear();
                        data.forEach(function (itemData) {
                          addItemToCanvas(itemData, itemData.xCm + itemData.widthCm / 2, itemData.yCm + itemData.heightCm / 2);
                        });
                        applyZoom();
                        updateTotals();
                      }
                    }
                  } catch (err) {
                    console.error("Failed to parse template code:", err);
                  }
                }
              } else {
                const pId = clone.getAttribute('data-product-id');
                const title = clone.getAttribute('data-product-title');
                const price = clone.getAttribute('data-product-price');
                const priceRaw = parseFloat(clone.getAttribute('data-product-price-raw'));
                const image = clone.getAttribute('data-product-image');
                const variantId = clone.getAttribute('data-variant-id');
                const widthCm = parseFloat(clone.getAttribute('data-product-width')) || 50;
                const heightCm = parseFloat(clone.getAttribute('data-product-height')) || 30;

                const itemData = {
                  productId: pId,
                  title: title,
                  price: price,
                  priceRaw: priceRaw,
                  image: image,
                  variantId: variantId,
                  widthCm: widthCm,
                  heightCm: heightCm
                };
                const pos = findNextPositionForClickedItem(widthCm, heightCm);
                if (pos && !isOverlapping(null, pos.x, pos.y, widthCm, heightCm)) {
                  addItemToCanvas(itemData, pos.x + widthCm / 2, pos.y + heightCm / 2);
                } else {
                  addItemToCanvas(itemData);
                }
              }

              if (mobileSubmenu) mobileSubmenu.classList.remove('is-active');
            });
            mobileSubmenuProducts.appendChild(clone);
          });

          // Set mobile submenu count to show only visible products!
          const visibleCount = Array.from(cards).filter(function (card) {
            return card.style.display !== 'none';
          }).length;
          if (mobileSubmenuCount) mobileSubmenuCount.textContent = visibleCount + ' Products';

          if (mobileSubmenu) mobileSubmenu.classList.add('is-active');
        }
      });
    });
  }

  if (mobileSubmenuBack) {
    mobileSubmenuBack.addEventListener('click', function () {
      if (mobileSubmenu) mobileSubmenu.classList.remove('is-active');
    });
  }

  if (mobileFooterDetailsBtn && mobileOrderDetail) {
    mobileFooterDetailsBtn.addEventListener('click', function () {
      mobileOrderDetail.classList.add('is-active');
    });
  }

  if (mobileOrderDetailClose && mobileOrderDetail) {
    mobileOrderDetailClose.addEventListener('click', function () {
      mobileOrderDetail.classList.remove('is-active');
    });
  }

  if (mobileFooterCheckoutBtn) {
    mobileFooterCheckoutBtn.addEventListener('click', handleCheckout);
  }
  if (mobileDrawerCheckoutBtn) {
    mobileDrawerCheckoutBtn.addEventListener('click', handleCheckout);
  }

  /* =============================================
     CHECKOUT FUNCTIONALITY (AJAX)
     ============================================= */
  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', handleCheckout);
  }
  if (detailsCheckoutBtn) {
    detailsCheckoutBtn.addEventListener('click', handleCheckout);
  }

  function handleCheckout() {
    if (state.items.length === 0) return;

    if (checkoutBtn) checkoutBtn.classList.add('disabled');
    if (detailsCheckoutBtn) detailsCheckoutBtn.classList.add('disabled');

    // Group items by variant ID to compute quantities
    const itemsToAdd = {};
    state.items.forEach(function (item) {
      if (!itemsToAdd[item.variantId]) {
        itemsToAdd[item.variantId] = 0;
      }
      itemsToAdd[item.variantId]++;
    });

    // Construct the Shopify cart permalink path: /cart/variant_id:qty,variant_id:qty
    const parts = [];
    Object.keys(itemsToAdd).forEach(function (variantId) {
      parts.push(variantId + ':' + itemsToAdd[variantId]);
    });

    const permalinkPath = '/cart/' + parts.join(',');

    // Clear saved composition on successful checkout redirection
    localStorage.removeItem('shelfmate_composition');

    // Redirect directly to the checkout page using the permalink
    window.location.href = permalinkPath;
  }

  /* =============================================
     HELPERS & PRICING
     ============================================= */
  function formatMoney(amountCents) {
    const val = (amountCents / 100).toFixed(2);
    if (currencySymbol === '€') {
      const formatted = currencySymbol + val.replace('.', ',');
      return formatted.endsWith(',00') ? formatted.slice(0, -3) : formatted;
    }
    const formatted = currencySymbol + val;
    return formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted;
  }

  function updateRulers() {
    if (!rulerTop || !rulerLeft) return;

    if (state.items.length === 0) {
      rulerTop.style.display = 'none';
      rulerLeft.style.display = 'none';
      if (badgeW) badgeW.textContent = '0';
      if (badgeH) badgeH.textContent = '0';
      return;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    state.items.forEach(function (item) {
      if (isNaN(item.xCm) || isNaN(item.yCm)) return;
      const left = item.xCm;
      const right = item.xCm + item.widthCm;
      const top = item.yCm;
      const bottom = item.yCm + item.heightCm;

      if (left < minX) minX = left;
      if (right > maxX) maxX = right;
      if (top < minY) minY = top;
      if (bottom > maxY) maxY = bottom;
    });

    if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) {
      rulerTop.style.display = 'none';
      rulerLeft.style.display = 'none';
      if (badgeW) badgeW.textContent = '0';
      if (badgeH) badgeH.textContent = '0';
      return;
    }

    const usedW = maxX - minX;
    const usedH = maxY - minY;

    if (badgeW) badgeW.textContent = Math.round(usedW);
    if (badgeH) badgeH.textContent = Math.round(usedH);

    // If rulers are toggled off, hide them but keep the badge updated
    if (!state.showRulers) {
      rulerTop.style.display = 'none';
      rulerLeft.style.display = 'none';
      return;
    }

    rulerTop.style.display = 'flex';
    rulerLeft.style.display = 'flex';

    const zoomFactor = pxPerCm * state.zoom;
    const minX_px = minX * zoomFactor;
    const maxX_px = maxX * zoomFactor;
    const minY_px = minY * zoomFactor;
    const maxY_px = maxY * zoomFactor;

    const width_px = maxX_px - minX_px;
    const height_px = maxY_px - minY_px;

    rulerTop.style.left = minX_px + 'px';
    rulerTop.style.width = width_px + 'px';
    if (labelW) {
      labelW.textContent = Math.round(usedW) + ' cm';
    }

    rulerLeft.style.top = minY_px + 'px';
    rulerLeft.style.height = height_px + 'px';
    if (labelH) {
      labelH.textContent = Math.round(usedH) + ' cm';
    }

    if (badgeW) badgeW.textContent = Math.round(usedW);
    if (badgeH) badgeH.textContent = Math.round(usedH);
  }

  function updateTotals() {
    let subtotal = 0;
    state.items.forEach(function (item) {
      subtotal += item.priceRaw;
    });

    let discountPercent = 0;
    if (allowDiscount) {
      if (subtotal >= 10000 && subtotal < 50000) {
        discountPercent = 0.10; // 10%
      } else if (subtotal >= 50000) {
        discountPercent = 0.20; // 20%
      }
    }

    const discountAmount = subtotal * discountPercent;
    const total = subtotal - discountAmount;

    // Checkout Header at the top of the sidebar stays visible
    if (checkoutHeader) {
      checkoutHeader.style.display = 'flex';
    }

    if (totalPriceLabel) {
      if (discountAmount > 0) {
        totalPriceLabel.innerHTML = formatMoney(total) + '<span class="shelfmate-total-original-price" style="text-decoration: line-through; color: #888; margin-left: var(--vw-8); font-weight: normal;">' + formatMoney(subtotal) + '</span>';
      } else {
        totalPriceLabel.innerHTML = formatMoney(total);
      }
    }

    if (checkoutBtn) {
      if (state.items.length > 0) {
        checkoutBtn.classList.remove('disabled');
        checkoutBtn.removeAttribute('disabled');
      } else {
        checkoutBtn.classList.add('disabled');
        checkoutBtn.setAttribute('disabled', 'true');
      }
    }

    // Update details drawer elements
    if (detailsCount) {
      detailsCount.textContent = 'Total ' + state.items.length + (state.items.length === 1 ? ' product' : ' products');
    }
    if (detailsSubtotal) {
      detailsSubtotal.textContent = formatMoney(subtotal);
    }
    if (detailsDiscountRow && detailsDiscount) {
      if (discountAmount > 0) {
        detailsDiscountRow.style.display = 'flex';
        detailsDiscount.textContent = '-' + formatMoney(discountAmount);
      } else {
        detailsDiscountRow.style.display = 'none';
      }
    }
    if (detailsTotal) {
      detailsTotal.textContent = formatMoney(total);
    }

    // Update Mobile Footer & Order Details elements
    if (mobileFooterPrice) {
      if (discountAmount > 0) {
        mobileFooterPrice.innerHTML = formatMoney(total) + '<span class="shelfmate-total-original-price" style="text-decoration: line-through; color: #888; margin-left: var(--m-vw-8); font-weight: normal;">' + formatMoney(subtotal) + '</span>';
      } else {
        mobileFooterPrice.innerHTML = formatMoney(total);
      }
    }

    const hasItems = state.items.length > 0;
    [mobileFooterCheckoutBtn, mobileDrawerCheckoutBtn].forEach(function (btn) {
      if (btn) {
        if (hasItems) {
          btn.classList.remove('disabled');
          btn.removeAttribute('disabled');
        } else {
          btn.classList.add('disabled');
          btn.setAttribute('disabled', 'true');
        }
      }
    });

    if (mobileOrderDetailCount) {
      mobileOrderDetailCount.textContent = 'Total ' + state.items.length + (state.items.length === 1 ? ' product' : ' products');
    }
    if (mobileOrderDetailSubtotal) {
      mobileOrderDetailSubtotal.textContent = formatMoney(subtotal);
    }
    if (mobileOrderDetailDiscountRow && mobileOrderDetailDiscount) {
      if (discountAmount > 0) {
        mobileOrderDetailDiscountRow.style.display = 'flex';
        mobileOrderDetailDiscount.textContent = '-' + formatMoney(discountAmount);
      } else {
        mobileOrderDetailDiscountRow.style.display = 'none';
      }
    }
    if (mobileOrderDetailTotal) {
      mobileOrderDetailTotal.textContent = formatMoney(total);
    }

    // Toggle Reset/Clear Wall button in toolbar
    if (resetBtn) {
      resetBtn.style.display = state.items.length > 0 ? 'block' : 'none';
    }

    // Update Floating Discount Popup Card
    if (discountPopupWrap) {
      if (state.items.length > 0 && allowDiscount) {
        if (!state.discountPopupClosed) {
          discountPopupWrap.style.display = 'flex';
        } else {
          discountPopupWrap.style.display = 'none';
        }
      } else {
        // Reset dismissal flag when canvas becomes empty so it can show again next time
        if (state.items.length === 0) {
          state.discountPopupClosed = false;
        }
        discountPopupWrap.style.display = 'none';
      }
    }

    updateRulers();
    renderDetailsItems();
    renderMobileDetailsItems();
    saveComposition();
  }

  function renderMobileDetailsItems() {
    if (!mobileOrderDetailItems) return;
    mobileOrderDetailItems.innerHTML = '';

    const groups = {};
    state.items.forEach(function (item) {
      const key = item.variantId;
      if (!groups[key]) {
        groups[key] = {
          name: item.title,
          priceRaw: item.priceRaw,
          image: item.image,
          qty: 0
        };
      }
      groups[key].qty++;
    });

    const variantIds = Object.keys(groups);
    const hasMoreThanThree = variantIds.length > 3;

    variantIds.forEach(function (variantId, index) {
      const group = groups[variantId];
      const row = document.createElement('div');
      row.className = 'shelfmate-details-item';

      if (hasMoreThanThree && state.detailsItemsCollapsed && index > 2) {
        row.style.display = 'none';
      }

      const lastVisibleIndex = (hasMoreThanThree && state.detailsItemsCollapsed) ? 2 : (variantIds.length - 1);
      if (index === lastVisibleIndex) {
        row.style.borderBottom = 'none';
      } else {
        row.style.borderBottom = '';
      }

      const info = document.createElement('div');
      info.className = 'shelfmate-details-item__info';

      if (group.image) {
        const imgWrapper = document.createElement('div');
        imgWrapper.className = 'shelfmate-details-item__img';
        const img = document.createElement('img');
        img.src = group.image;
        img.alt = group.name;
        imgWrapper.appendChild(img);
        info.appendChild(imgWrapper);
      }

      const text = document.createElement('div');
      text.className = 'shelfmate-details-item__text';

      const nameSpan = document.createElement('span');
      nameSpan.className = 'shelfmate-details-item__name';
      nameSpan.textContent = group.name;
      text.appendChild(nameSpan);

      const priceSpan = document.createElement('span');
      priceSpan.className = 'shelfmate-details-item__price';
      priceSpan.textContent = formatMoney(group.priceRaw * group.qty);
      text.appendChild(priceSpan);

      const qtySpan = document.createElement('span');
      qtySpan.className = 'shelfmate-details-item__qty';
      qtySpan.textContent = 'Quantity: ' + group.qty;
      text.appendChild(qtySpan);

      info.appendChild(text);
      row.appendChild(info);

      mobileOrderDetailItems.appendChild(row);
    });

    if (mobileDetailsMoreWrapper) {
      if (hasMoreThanThree) {
        mobileDetailsMoreWrapper.style.display = 'flex';
        mobileDetailsMoreWrapper.classList.add('is-visible');
        if (mobileDetailsMoreBtn) {
          const btnText = mobileDetailsMoreBtn.querySelector('span');
          if (btnText) {
            btnText.textContent = state.detailsItemsCollapsed ? 'Show More' : 'Show Less';
          }
          if (state.detailsItemsCollapsed) {
            mobileDetailsMoreBtn.classList.remove('is-active');
          } else {
            mobileDetailsMoreBtn.classList.add('is-active');
          }
        }
      } else {
        mobileDetailsMoreWrapper.style.display = 'none';
        mobileDetailsMoreWrapper.classList.remove('is-visible');
      }
    }
  }

  function renderDetailsItems() {
    if (!detailsItemsContainer) return;
    detailsItemsContainer.innerHTML = '';

    const groups = {};
    state.items.forEach(function (item) {
      const key = item.variantId;
      if (!groups[key]) {
        groups[key] = {
          name: item.title,
          priceRaw: item.priceRaw,
          image: item.image,
          qty: 0
        };
      }
      groups[key].qty++;
    });

    const variantIds = Object.keys(groups);
    const hasMoreThanThree = variantIds.length > 3;

    variantIds.forEach(function (variantId, index) {
      const group = groups[variantId];
      const row = document.createElement('div');
      row.className = 'shelfmate-details-item';

      if (hasMoreThanThree && state.detailsItemsCollapsed && index > 2) {
        row.style.display = 'none';
      }

      const info = document.createElement('div');
      info.className = 'shelfmate-details-item__info';

      if (group.image) {
        const imgWrapper = document.createElement('div');
        imgWrapper.className = 'shelfmate-details-item__img';
        const img = document.createElement('img');
        img.src = group.image;
        img.alt = group.name;
        imgWrapper.appendChild(img);
        info.appendChild(imgWrapper);
      }

      const text = document.createElement('div');
      text.className = 'shelfmate-details-item__text';

      const nameSpan = document.createElement('span');
      nameSpan.className = 'shelfmate-details-item__name';
      nameSpan.textContent = group.name;
      text.appendChild(nameSpan);

      const priceSpan = document.createElement('span');
      priceSpan.className = 'shelfmate-details-item__price';
      priceSpan.textContent = formatMoney(group.priceRaw * group.qty);
      text.appendChild(priceSpan);

      const qtySpan = document.createElement('span');
      qtySpan.className = 'shelfmate-details-item__qty';
      qtySpan.textContent = 'Quantity: ' + group.qty;
      text.appendChild(qtySpan);

      info.appendChild(text);
      row.appendChild(info);

      detailsItemsContainer.appendChild(row);
    });

    if (detailsMoreWrapper) {
      if (hasMoreThanThree) {
        detailsMoreWrapper.style.display = 'flex';
        detailsMoreWrapper.classList.add('is-visible');
        if (detailsMoreBtn) {
          const btnText = detailsMoreBtn.querySelector('span');
          if (btnText) {
            btnText.textContent = state.detailsItemsCollapsed ? 'Show More' : 'Show Less';
          }
          if (state.detailsItemsCollapsed) {
            detailsMoreBtn.classList.remove('is-active');
          } else {
            detailsMoreBtn.classList.add('is-active');
          }
        }
      } else {
        detailsMoreWrapper.style.display = 'none';
        detailsMoreWrapper.classList.remove('is-visible');
      }
    }
  }

  function saveComposition() {
    const dataToSave = state.items.map(function (item) {
      return {
        productId: item.productId,
        variantId: item.variantId,
        widthCm: item.widthCm,
        heightCm: item.heightCm,
        xCm: item.xCm,
        yCm: item.yCm
      };
    });
    const serialized = JSON.stringify(dataToSave);
    localStorage.setItem('shelfmate_composition', serialized);
    console.log("Shelfmate Current Canvas JSON Code:", serialized);
  }

  function loadComposition() {
    const saved = localStorage.getItem('shelfmate_composition');
    if (!saved) return;
    try {
      const data = JSON.parse(saved);
      if (Array.isArray(data)) {
        data.forEach(function (itemData) {
          const sidebarCard = document.querySelector(`.shelfmate-product-card[data-variant-id="${itemData.variantId}"]`);
          if (sidebarCard) {
            itemData.title = sidebarCard.getAttribute('data-product-title') || '';
            itemData.price = sidebarCard.getAttribute('data-product-price') || '';
            itemData.priceRaw = parseFloat(sidebarCard.getAttribute('data-product-price-raw')) || 0;
            itemData.image = sidebarCard.getAttribute('data-product-image') || '';
          } else {
            itemData.title = itemData.title || '';
            itemData.price = itemData.price || '';
            itemData.priceRaw = itemData.priceRaw || 0;
            itemData.image = itemData.image || '';
          }
          addItemToCanvas(itemData, itemData.xCm + itemData.widthCm / 2, itemData.yCm + itemData.heightCm / 2);
        });
      }
    } catch (e) {
      console.error("Failed to load composition", e);
    }
  }

  function updateHint() {
    if (!canvasHint) return;
    if (state.items.length > 0) {
      canvasHint.classList.add('hidden');
    } else {
      canvasHint.classList.remove('hidden');
    }
  }

  function clampAllItems() {
    state.items.forEach(function (item) {
      const renderX = Math.min(item.xCm, Math.max(0, state.canvasW - item.widthCm));
      const renderY = Math.min(item.yCm, Math.max(0, state.canvasH - item.heightCm));
      item.el.style.left = (renderX * pxPerCm * state.zoom) + 'px';
      item.el.style.top = (renderY * pxPerCm * state.zoom) + 'px';
      item.el.style.width = (item.widthCm * pxPerCm * state.zoom) + 'px';
      item.el.style.height = (item.heightCm * pxPerCm * state.zoom) + 'px';
    });
  }

  /* =============================================
     TUTORIAL WALKTHROUGH (IntroJS)
     ============================================= */
  function displayTutorial() {
    setTimeout(function () {
      if (typeof introJs === 'undefined') {
        console.warn('IntroJS script is not loaded.');
        return;
      }
      const isMobile = window.innerWidth <= 750;
      const step1El = document.querySelector(isMobile ? '#step-mobile-1' : '#step-desktop-1');
      const step2El = document.querySelector(isMobile ? '#step-mobile-2' : '#step-desktop-2');

      const title1 = app ? (app.getAttribute('data-text-title-tutorial-1') || 'Step 1') : 'Step 1';
      const desc1 = app ? (app.getAttribute('data-text-desc-tutorial-1') || 'Select a shelf type to start customizing') : 'Select a shelf type to start customizing';
      const title2 = app ? (app.getAttribute('data-text-title-tutorial-2') || 'Step 2') : 'Step 2';
      const desc2 = app ? (app.getAttribute('data-text-desc-tutorial-2') || 'Or select a pre-made template') : 'Or select a pre-made template';

      const steps = [];
      if (step1El) {
        steps.push({
          element: step1El,
          intro: '<b>' + title1 + '</b><p>' + desc1 + '</p>',
          position: isMobile ? 'bottom' : 'left',
          tooltipClass: 'myTooltipClass',
          highlightClass: 'myHighlightClass'
        });
      }
      if (step2El) {
        steps.push({
          element: step2El,
          intro: '<b>' + title2 + '</b><p>' + desc2 + '</p>',
          position: isMobile ? 'bottom' : 'left'
        });
      }

      if (steps.length === 0) return;

      const intro = introJs();

      intro.setOptions({
        steps: steps,
        showStepNumbers: true,
        exitOnOverlayClick: false,
        nextLabel:
          'Next <img src="https://images.ctfassets.net/hhqcfrz88uh8/D3dBHm7b2HSUc7wrw46ir/07adb09c629899282947983b2b0b85f9/arrow-right__1_.svg" alt="arrow" />',
        prevLabel: 'Back',
        skipLabel: 'Skip',
        doneLabel:
          'Finish <img src="https://images.ctfassets.net/hhqcfrz88uh8/D3dBHm7b2HSUc7wrw46ir/07adb09c629899282947983b2b0b85f9/arrow-right__1_.svg" alt="arrow" />'
      });

      intro.onstart(function () {
        document.body.classList.add('shelfmate-tutorial-active');
      });

      intro.onbeforechange(function (targetElement) {
        if (!targetElement) return;
        if (isMobile) {
          const menuWrapper = document.getElementById('shelfmate-mobile-menu-wrapper');
          const step2 = document.getElementById('step-mobile-2');
          const isStep2 = (targetElement.id === 'step-mobile-2' || targetElement.closest('#step-mobile-2') || targetElement === step2);
          if (isStep2) {
            if (menuWrapper) {
              menuWrapper.scrollLeft = menuWrapper.scrollWidth;
            }
            if (step2) {
              step2.scrollIntoView({ block: 'nearest', inline: 'end', behavior: 'instant' });
            }
          } else {
            if (menuWrapper) {
              menuWrapper.scrollLeft = 0;
            }
          }
        }
      });

      intro.onchange(function (targetElement) {
        if (!targetElement) return;
        if (isMobile) {
          const menuWrapper = document.getElementById('shelfmate-mobile-menu-wrapper');
          const step2 = document.getElementById('step-mobile-2');
          const isStep2 = (targetElement.id === 'step-mobile-2' || targetElement.closest('#step-mobile-2') || targetElement === step2);

          if (isStep2) {
            if (menuWrapper) {
              menuWrapper.scrollLeft = menuWrapper.scrollWidth;
            }
            if (step2) {
              step2.scrollIntoView({ block: 'nearest', inline: 'end', behavior: 'instant' });
            }
          } else {
            if (menuWrapper) {
              menuWrapper.scrollLeft = 0;
            }
          }

          const tooltip = document.querySelector('.introjs-tooltip');
          if (tooltip) {
            if (isStep2) {
              tooltip.classList.add('introjs-step-template');
            } else {
              tooltip.classList.remove('introjs-step-template');
            }
          }

          setTimeout(function () {
            if (intro.refresh) intro.refresh();
          }, 30);
        }
      });

      intro.oncomplete(function () {
        document.body.classList.remove('shelfmate-tutorial-active');
        localStorage.setItem('tutorial-shelfmate-app', 'true');
      });

      intro.onexit(function () {
        document.body.classList.remove('shelfmate-tutorial-active');
        localStorage.setItem('tutorial-shelfmate-app', 'true');
      });

      intro.setOptions({
        steps: steps,
        showStepNumbers: true,
        exitOnOverlayClick: false,
        nextLabel: 'Next <img src="https://images.ctfassets.net/hhqcfrz88uh8/D3dBHm7b2HSUc7wrw46ir/07adb09c629899282947983b2b0b85f9/arrow-right__1_.svg" alt="arrow" />',
        prevLabel: 'Back',
        skipLabel: 'Skip',
        doneLabel: 'Finish <img src="https://images.ctfassets.net/hhqcfrz88uh8/D3dBHm7b2HSUc7wrw46ir/07adb09c629899282947983b2b0b85f9/arrow-right__1_.svg" alt="arrow" />'
      });

      intro.start();
    }, 1000);
  }

  window.displayShelfmateTutorial = displayTutorial;

  // Load composition and calculate initial totals
  loadComposition();
  applyZoom();
  updateTotals();

  // Show app once initialization is complete
  if (app) {
    app.classList.add('loaded');
  }

  if (localStorage.getItem('tutorial-shelfmate-app') !== 'true') {
    displayTutorial();
  }

})();
